import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { extractFullScript } from '../lib/contentParsers';

interface SharedScriptData {
  title: string | null;
  subtitle: string | null;
  script: string | null;
}

type LoadState = 'loading' | 'success' | 'error';

export function SharedScript() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedScriptData | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!token) {
      setState('error');
      return;
    }

    const fetchScript = async () => {
      try {
        const res = await fetch(`/api/script/${encodeURIComponent(token)}`);
        if (!res.ok) {
          setState('error');
          return;
        }
        const json = await res.json();
        setData(json);
        setState('success');
      } catch {
        setState('error');
      }
    };

    fetchScript();
  }, [token]);

  const scriptMarkdown = useMemo(
    () => extractFullScript(data?.script ?? ''),
    [data?.script],
  );

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error / not found state
  if (state === 'error' || !data) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-1.414l4.5-4.5a4.5 4.5 0 00-6.364-6.364l-1.757 1.757" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            This link is no longer available
          </h2>
          <p className="text-slate-400">
            The script you're looking for may have been removed or the share link has been revoked.
          </p>
        </motion.div>
      </div>
    );
  }

  // Success — render the script
  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Fixed background gradient */}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-[#020617] to-[#020617]" />

      {/* Blue accent line at top */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      <div className="max-w-3xl mx-auto px-4 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Title block */}
          <div className="text-center mb-10">
            {data.title && (
              <h1
                className="text-3xl md:text-4xl font-bold text-white mb-3"
                style={{ textShadow: '0 0 30px rgba(37,99,235,0.3)' }}
              >
                {data.title}
              </h1>
            )}
            {data.subtitle && (
              <p className="text-lg text-slate-400">{data.subtitle}</p>
            )}
          </div>

          {/* Script content card */}
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 md:p-10">
            <div className="prose prose-invert prose-lg max-w-none leading-relaxed prose-headings:text-slate-100 prose-headings:font-semibold prose-p:text-slate-300 prose-strong:text-white prose-li:text-slate-300 prose-hr:border-slate-700/50">
              <ReactMarkdown>{scriptMarkdown}</ReactMarkdown>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-10">
            <p className="text-sm text-slate-500">
              Powered by{' '}
              <span className="text-slate-400 font-medium">Trained Advisor</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
